import { useCallback, useMemo } from 'react';
import { User, RegisterPayload } from '@/types/auth';
import { getCurrentUser, updateUserTheme } from '@/services/auth.service';
import { supabase } from '@/integrations/supabase/client';
import logger from '@/utils/logger';
import {
  safeToastError,
  safeToastSuccess,
  safeToastInfo
} from '@/components/ui/toastManager';
import { secureSignIn, secureSignUp, secureResetPassword } from '@/services/auth/secureAuth.service';
import { handleRateLimitError } from '@/utils/secureErrorHandler';
import { networkService } from '@/services/network.service';
import { postLoginCoordinator } from '@/services/auth/postLoginCoordinator';
import { trackRegistration } from '@/lib/facebook';
import {
  signOut as signOutApi,
  signOutAllDevices as signOutAllDevicesApi
} from '@/services/auth/auth.api';
import { DEVICE_APP_VERSION_SYNC_STORAGE_KEY } from '@/services/auth/device.service';
import { isAndroidNative } from '@/utils/platform';

const AUTH_TIMEOUT_MS = 15000;
const env =
  (typeof import.meta !== 'undefined' && (import.meta as any).env) || process.env;
const CHECK_SUBSCRIPTION_TIMEOUT_MS =
  Number(env?.VITE_CHECK_SUBSCRIPTION_TIMEOUT_MS) || AUTH_TIMEOUT_MS;
const REGISTER_DEVICE_TIMEOUT_MS =
  Number(env?.VITE_REGISTER_DEVICE_TIMEOUT_MS) || AUTH_TIMEOUT_MS;

// Helper puri: nessuna dipendenza da stato/hook, quindi a scope di modulo
// così i metodi sotto possono essere stabilizzati con useCallback senza
// catturare riferimenti che cambiano ad ogni render.
const withTimeout = <T>(promise: Promise<T>, ms = AUTH_TIMEOUT_MS) =>
  Promise.race<T>([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error('Timeout')), ms))
  ]);

const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  retries = 2,
  delay = 500
): Promise<T> => {
  try {
    return await fn();
  } catch (error: any) {
    if (retries > 0 && error?.message === 'Timeout') {
      await new Promise((resolve) => setTimeout(resolve, delay));
      return retryWithBackoff(fn, retries - 1, delay * 2);
    }
    throw error;
  }
};

export const useAuthMethods = () => {
  // Secure login implementation
  const login = useCallback(async (
    email: string,
    password: string,
    remember?: boolean
  ): Promise<{ success: boolean; error?: string; handled?: boolean }> => {
    logger.log(`Tentativo di login con email: ${email}, remember: ${remember}`);

    try {
      const result = await secureSignIn(email, password);

      if (!result.success) {
        postLoginCoordinator.end();
        if (result.remainingTime) {
          return { success: false, error: handleRateLimitError(result.remainingTime) };
        }
        return { success: false, error: result.error || 'Errore di autenticazione' };
      }

      postLoginCoordinator.start();
      setTimeout(() => postLoginCoordinator.end(), 5000);

      localStorage.deviceRegistrationPending = Date.now().toString();

      const runPostLoginTasks = async () => {
        let deviceRegId: string | undefined;
        try {
          const { registerDevice } = await import('@/services/auth/device.service');

          // Su Android il premium arriva da RevenueCat (webhook → is_premium):
          // saltiamo check-subscription (Stripe) per non azzerarlo. Vedi #1334.
          if (!isAndroidNative()) {
            const subCheckId = crypto.randomUUID();
            logger.log('check-subscription start', {
              id: subCheckId,
              timestamp: new Date().toISOString()
            });
            withTimeout(
              supabase.functions.invoke('check-subscription'),
              CHECK_SUBSCRIPTION_TIMEOUT_MS
            )
              .then(() => {
                logger.log('check-subscription end', {
                  id: subCheckId,
                  timestamp: new Date().toISOString()
                });
              })
              .catch((subCheckErr: any) => {
                if (subCheckErr?.message === 'Timeout') {
                  const networkState = networkService.isOnline();
                  logger.warn("Timeout nel controllo dell'abbonamento", {
                    id: subCheckId,
                    timestamp: new Date().toISOString(),
                    networkState,
                    error: subCheckErr
                  });
                  safeToastError("Timeout nel controllo dell'abbonamento");
                } else {
                  logger.error("Errore nel controllo dell'abbonamento dopo il login", {
                    id: subCheckId,
                    error: subCheckErr
                  });
                }
              });
          }

          deviceRegId = crypto.randomUUID();
          logger.log('registerDevice start', {
            id: deviceRegId,
            timestamp: new Date().toISOString()
          });
          await retryWithBackoff(() =>
            withTimeout(registerDevice(), REGISTER_DEVICE_TIMEOUT_MS)
          );
          logger.log('registerDevice end', {
            id: deviceRegId,
            timestamp: new Date().toISOString()
          });
          postLoginCoordinator.markDeviceRegistered();
        } catch (deviceRegErr: any) {
          if (deviceRegErr?.message === 'Timeout') {
            const networkState = networkService.isOnline();
            logger.warn('Timeout nella registrazione del dispositivo', {
              id: deviceRegId,
              timestamp: new Date().toISOString(),
              networkState,
              error: deviceRegErr
            });
            safeToastError('Timeout nella registrazione del dispositivo');
          } else if (deviceRegErr) {
            logger.error('Errore nella registrazione del dispositivo', {
              id: deviceRegId,
              error: deviceRegErr
            });
          }
        } finally {
          localStorage.removeItem('deviceRegistrationPending');
        }
      };

      if (networkService.isOnline()) {
        void runPostLoginTasks();
      } else {
        const unsubscribe = networkService.addListener(({ connected }) => {
          if (connected) {
            unsubscribe();
            void runPostLoginTasks();
          }
        });
      }

      return { success: true };
    } catch (error: any) {
      postLoginCoordinator.end();
      logger.error("Errore durante il login:", error);
      safeToastError('Errore di autenticazione');
      return { success: false, error: 'Errore di autenticazione' };
    }
  }, []);

  // Secure registration implementation
  const register = useCallback(async (email: string, password: string, name: string): Promise<boolean> => {
    logger.log(`Tentativo di registrazione con email: ${email}, name: ${name}`);
    
    try {
      const result = await secureSignUp(email, password, name);
      
      if (!result.success) {
        if (result.remainingTime) {
          safeToastError(handleRateLimitError(result.remainingTime));
        } else {
          safeToastError(result.error || "Errore durante la registrazione");
        }
        return false;
      }
      await trackRegistration();
      return true;
    } catch (error: any) {
      logger.error("Errore durante la registrazione:", error);
      safeToastError("Errore durante la registrazione");
      return false;
    }
  }, []);

  // Secure logout implementation
  const logout = useCallback(async (): Promise<void> => {
    logger.log('Tentativo di logout');

    const cleanup = () => {
      // Clear device registration timestamp to avoid stale checks after logout
      localStorage.removeItem('deviceRegisteredAt');
      localStorage.removeItem('deviceRegistrationPending');
      localStorage.removeItem(DEVICE_APP_VERSION_SYNC_STORAGE_KEY);
    };

    try {
      await signOutApi();
      cleanup();
      safeToastSuccess('Disconnessione avvenuta', { description: '' });
    } catch (error: any) {
      const message = (error?.message || '').toLowerCase();
      const isFetchFailure =
        message.includes('failed to fetch') ||
        message.includes('errore di connessione');
      const noSession =
        message.includes('session') &&
        (message.includes('no') ||
          message.includes('not') ||
          message.includes('missing') ||
          message.includes('nessuna'));

      if (isFetchFailure || noSession) {
        cleanup();
        safeToastInfo('Disconnessione completata');
        return;
      }

      logger.error('Errore durante il logout:', error);
      safeToastError('Errore durante la disconnessione');
    }
  }, []);

  // Alias per logout per compatibilità
  const signOut = useCallback(async (): Promise<void> => {
    await logout();
  }, [logout]);

  // Logout from all devices
  const signOutAllDevices = useCallback(async (): Promise<void> => {
    logger.log('Tentativo di logout da tutti i dispositivi');
    try {
      await signOutAllDevicesApi();
      localStorage.removeItem('deviceRegisteredAt');
      localStorage.removeItem('deviceRegistrationPending');
      localStorage.removeItem(DEVICE_APP_VERSION_SYNC_STORAGE_KEY);
      safeToastSuccess('Disconnessione da tutti i dispositivi', { description: '' });
    } catch (error: any) {
      logger.error('Errore durante il logout globale:', error);
      safeToastError('Errore durante la disconnessione');
    }
  }, []);

  // Secure password reset implementation
  const resetPassword = useCallback(async (email: string): Promise<boolean> => {
    logger.log(`Richiesta reset password per: ${email}`);
    
    try {
      const result = await secureResetPassword(email);
      
      if (!result.success) {
        if (result.remainingTime) {
          safeToastError(handleRateLimitError(result.remainingTime));
        } else {
          safeToastError(result.error || "Errore durante la richiesta di reset password");
        }
        return false;
      }
      
      safeToastSuccess("Email per il reset della password inviata", { description: "" });
      return true;
    } catch (error: any) {
      logger.error("Errore durante la richiesta di reset password:", error);
      safeToastError("Errore durante la richiesta di reset password");
      return false;
    }
  }, []);

  const fetchCurrentUser = useCallback(async (): Promise<User | null> => {
    logger.log('Recupero utente corrente');
    try {
      return await getCurrentUser();
    } catch (error) {
      logger.error("Errore durante il recupero dell'utente:", error);
      return null;
    }
  }, []);

  const refreshUser = useCallback(async (): Promise<User | null> => {
    logger.log('Aggiornamento dati utente');
    try {
      // Su Android l'entitlement è gestito da RevenueCat: niente check-subscription
      // (Stripe), che farebbe downgrade del premium. Vedi #1334.
      if (networkService.isOnline() && !isAndroidNative()) {
        try {
          await retryWithBackoff(() =>
            withTimeout(
              supabase.functions.invoke('check-subscription'),
              CHECK_SUBSCRIPTION_TIMEOUT_MS
            )
          );
        } catch (err: any) {
          if (err?.message === 'Timeout') {
            safeToastError("Timeout nel controllo dell'abbonamento");
          } else {
            logger.error(
              "Errore nel controllo dell'abbonamento durante refreshUser:",
              err
            );
          }
        }
      }

      // Recuperiamo i dati utente aggiornati
      return await fetchCurrentUser();
    } catch (error) {
      logger.error("Errore durante il refresh dell'utente:", error);
      return null;
    }
  }, [fetchCurrentUser]);

  const updatePassword = useCallback(async (newPassword: string): Promise<boolean> => {
    logger.log(`Aggiornamento password: ${newPassword.slice(0, 1)}*****`);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        throw error;
      }
      
      safeToastSuccess("Password aggiornata con successo", { description: "" });
      return true;
    } catch (error: any) {
      logger.error("Errore durante l'aggiornamento della password:", error);
      safeToastError("Errore durante l'aggiornamento della password");
      return false;
    }
  }, []);

  const setThemePreference = useCallback(async (themeId: string): Promise<boolean> => {
    logger.log(`Impostazione tema preferito themeId: ${themeId}`);
    try {
      await updateUserTheme(themeId);
      return true;
    } catch (error) {
      logger.error("Errore durante l'aggiornamento delle preferenze di tema:", error);
      return false;
    }
  }, []);

  // Oggetto memoizzato: riferimento stabile finché i metodi (tutti useCallback)
  // non cambiano. Evita di invalidare il value di AuthContext ad ogni render. (#1425)
  return useMemo(
    () => ({
      login,
      register,
      logout,
      signOut,
      signOutAllDevices,
      refreshUser,
      updatePassword,
      resetPassword,
      setThemePreference,
      fetchCurrentUser
    }),
    [
      login,
      register,
      logout,
      signOut,
      signOutAllDevices,
      refreshUser,
      updatePassword,
      resetPassword,
      setThemePreference,
      fetchCurrentUser
    ]
  );
};
